/**
 * Fixed timestep loop with an accumulator and render interpolation.
 *
 * Simulation advances in constant slices so behaviour is identical on a 60 Hz
 * laptop and a 144 Hz desktop. Rendering interpolates between the last two sim
 * states so motion stays smooth without touching the simulation rate.
 */
export interface LoopHandlers {
  /** Advance the simulation by exactly `stepMs`. */
  step(): void;
  /** `alpha` is 0..1 between the previous and current simulation state. */
  render(alpha: number): void;
}

export interface LoopOptions {
  stepMs?: number;
  /** Cap on catch-up steps per frame. Prevents a spiral of death after a stall. */
  maxSteps?: number;
}

export interface Loop {
  start(): void;
  stop(): void;
  readonly running: boolean;
}

export function createLoop(handlers: LoopHandlers, options: LoopOptions = {}): Loop {
  const stepMs = options.stepMs ?? 1000 / 60;
  const maxSteps = options.maxSteps ?? 5;

  let raf = 0;
  let last = 0;
  let accumulator = 0;
  let running = false;

  const frame = (now: number): void => {
    raf = requestAnimationFrame(frame);
    // Clamp the delta: a backgrounded tab can hand back a delta of minutes.
    const delta = Math.min(now - last, stepMs * maxSteps);
    last = now;
    accumulator += delta;

    let steps = 0;
    while (accumulator >= stepMs && steps < maxSteps) {
      handlers.step();
      accumulator -= stepMs;
      steps++;
    }
    if (steps === maxSteps) accumulator = 0;

    handlers.render(accumulator / stepMs);
  };

  return {
    get running() {
      return running;
    },
    start() {
      if (running) return;
      running = true;
      last = performance.now();
      accumulator = 0;
      raf = requestAnimationFrame(frame);
    },
    stop() {
      if (!running) return;
      running = false;
      cancelAnimationFrame(raf);
    },
  };
}
